import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../i18n/LanguageContext";

// Il modulo viene montato solo quando è aperto, così la posizione preselezionata vale a ogni apertura
const ApplyModal = ({ onClose, preselectedPosition }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState({
        nome: "",
        posizione: preselectedPosition || "",
        email_candidato: "",
        curriculum: null,
        privacy: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const closeRef = useRef(null);

    // Comparsa con transizione, pagina bloccata sotto e chiusura con Esc
    useEffect(() => {
        const raf = requestAnimationFrame(() => setOpen(true));
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        return () => {
            cancelAnimationFrame(raf);
            document.body.style.overflow = "";
            document.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    // Il focus va sul pulsante di chiusura quando il modulo è già visibile (dopo l'inizio della transizione)
    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => closeRef.current && closeRef.current.focus(), 60);
        return () => clearTimeout(timer);
    }, [open]);

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === "file" && files && files[0]) {
            const file = files[0];
            const maxSize = 5 * 1024 * 1024; // 5MB
            const allowedTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

            if (file.size > maxSize) {
                alert(t("applyModal.fileTooLarge"));
                e.target.value = ""; // Reset input
                return;
            }
            if (!allowedTypes.includes(file.type)) {
                alert(t("applyModal.fileTypeInvalid"));
                e.target.value = ""; // Reset input
                return;
            }
        }

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : type === "file" ? files[0] : value,
        }));
    };

    const handleSubmit = (e) => {
        if (!formData.curriculum) {
            e.preventDefault();
            alert(t("applyModal.cvRequired"));
            return;
        }

        if (!formData.privacy) {
            e.preventDefault();
            alert(t("applyModal.privacyRequired"));
            return;
        }
        setIsSubmitting(true);
    };

    const ready = formData.privacy && formData.curriculum && !isSubmitting;

    return (
        <div className={`vmodal amodal${open ? " open" : ""}`} role="dialog" aria-modal="true" aria-labelledby="apply-title"
            onClick={(e) => e.target === e.currentTarget && onClose()}>
            <form
                action={`https://formsubmit.co/${process.env.REACT_APP_APPLY_EMAIL}`}
                method="POST"
                encType="multipart/form-data"
                onSubmit={handleSubmit}
            >
                <input type="hidden" name="_subject" value={`Nuova Candidatura: ${formData.posizione} - ${formData.nome}`} />
                <input type="hidden" name="_replyto" value={formData.email_candidato} />
                <input type="hidden" name="_autoresponse" value={t("applyModal.autoresponse")} />
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_template" value="table" />
                <input type="hidden" name="_next" value={typeof window !== 'undefined' ? window.location.href : ""} />

                <h3 id="apply-title">{t("applyModal.title")}</h3>
                <button type="button" className="aclose" ref={closeRef} onClick={onClose} aria-label={t("applyModal.close")}>
                    <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>

                <div className="field">
                    <label htmlFor="apply-name">{t("applyModal.fullName")} *</label>
                    <input type="text" id="apply-name" name="nome" required value={formData.nome} onChange={handleChange}
                        maxLength={50} autoComplete="name" placeholder={t("applyModal.namePlaceholder")} />
                </div>

                <div className="field">
                    <label htmlFor="apply-position">{t("applyModal.position")} *</label>
                    <select id="apply-position" name="posizione" required value={formData.posizione} onChange={handleChange}>
                        <option value="" disabled>{t("applyModal.selectPosition")}</option>
                        {t("workWithUs.positions").map((job) => (
                            <option key={job.id} value={job.id}>{job.title}</option>
                        ))}
                    </select>
                </div>

                <div className="field">
                    <label htmlFor="apply-email">{t("applyModal.email")} *</label>
                    <input type="email" id="apply-email" name="email_candidato" required value={formData.email_candidato} onChange={handleChange}
                        maxLength={50} autoComplete="email" placeholder={t("applyModal.emailPlaceholder")} />
                </div>

                <div className="field">
                    <span className="flabel">{t("applyModal.uploadCv")} *</span>
                    <input type="file" id="apply-cv" name="curriculum" accept=".pdf,.doc,.docx" onChange={handleChange} className="upload-input" />
                    <label htmlFor="apply-cv" className={`upload${formData.curriculum ? " has-file" : ""}`}>
                        <svg viewBox="0 0 24 24"><path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" /></svg>
                        <span>{formData.curriculum ? formData.curriculum.name : t("applyModal.uploadCvHint")}</span>
                    </label>
                </div>

                <label className="check">
                    <input type="checkbox" name="privacy" checked={formData.privacy} onChange={handleChange} />
                    <span>
                        {t.rich("applyModal.privacy", {
                            link: (chunk) => <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">{chunk}</a>,
                        })}
                    </span>
                </label>

                <button type="submit" className="btn btn-dark submit" disabled={!ready}>
                    {isSubmitting ? t("applyModal.submitting") : t("applyModal.submit")}
                </button>
            </form>
        </div>
    );
};

export default ApplyModal;
